import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FamilleData } from '../models/models';
import { GeneriqueDataType } from '../constants/enums';
import api from './api';

export type FamilleAutocompleteSlot = 'main' | 'parent';

interface FamilleState {
  familleData: FamilleData[];
  autocompleteMain: FamilleData[];
  autocompleteParent: FamilleData[];
  tree: FamilleData[];
  allFamilles: FamilleData[];
  allFamillesType: GeneriqueDataType | null; // type chargé dans allFamilles (évite d'afficher l'arbre d'un autre type)
  categoryEntities: FamilleData[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  autocompleteLoading: boolean;
  treeLoading: boolean;
  allFamillesLoading: boolean;
  categoryEntitiesLoading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedFamille: FamilleData | null;
}

const initialState: FamilleState = {
  familleData: [],
  autocompleteMain: [],
  autocompleteParent: [],
  tree: [],
  allFamilles: [],
  allFamillesType: null,
  categoryEntities: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  loading: false,
  autocompleteLoading: false,
  treeLoading: false,
  allFamillesLoading: false,
  categoryEntitiesLoading: false,
  error: null,
  successMessage: null,
  selectedFamille: null,
};

// AUTOCOMPLETE — deux slots indépendants : 'main' (sélection famille) et 'parent' (famille parente)
export const fetchFamillesAutocomplete = createAsyncThunk<
  { data: FamilleData[]; slot: FamilleAutocompleteSlot },
  { nom: string; excludeId?: number; slot: FamilleAutocompleteSlot; type: GeneriqueDataType },
  { rejectValue: string }
>(
  'famille/fetchFamillesAutocomplete',
  async ({ nom, excludeId, slot, type }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getAll', {
        params: { type, nom, visible: null, limit: 20, offset: 0 },
      });
      const data: FamilleData[] = response.data.data ?? [];
      const filtered = excludeId ? data.filter((f) => f.id !== excludeId) : data;
      return { data: filtered, slot };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur autocomplete.');
    }
  }
);

// GET ALL — sp_select_familles
export const fetchFamilles = createAsyncThunk<
  { data: FamilleData[]; total: number; totalPages: number; currentPage: number },
  { nom?: string | null; visible?: boolean | null; limit: number; offset: number; type: GeneriqueDataType },
  { rejectValue: string }
>(
  'famille/fetchFamilles',
  async ({ nom = null, visible = null, limit, offset, type }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getAll', {
        params: { type, nom, visible, limit, offset },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement.');
    }
  }
);

// GET BY PARENT — sp_select_familles_byParent
export const fetchFamillesByParent = createAsyncThunk<
  { data: FamilleData[]; total: number; totalPages: number; currentPage: number },
  { parent_id: number | null; visible?: boolean | null; limit: number; offset: number; type: GeneriqueDataType },
  { rejectValue: string }
>(
  'famille/fetchFamillesByParent',
  async ({ parent_id, visible = null, limit, offset, type }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getByParent', {
        params: { type, parent_id, visible, limit, offset },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement.');
    }
  }
);

// GET TREE — sp_select_familles_tree
export const fetchFamilleTree = createAsyncThunk<
  FamilleData[],
  { famille_id: number; type: GeneriqueDataType },
  { rejectValue: string }
>(
  'famille/fetchFamilleTree',
  async ({ famille_id, type }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getTree', {
        params: { type, famille_id },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement de l\'arbre.');
    }
  }
);

// ARBORESCENCE COMPLÈTE — toutes les familles d'un type (sp_select_familles),
// l'arbre est reconstruit côté client via parent_id. Slot dédié (allFamilles)
// pour ne pas écraser familleData utilisé par FamilleHierarchie.
export const fetchAllFamillesTree = createAsyncThunk<
  FamilleData[],
  { type: GeneriqueDataType },
  { rejectValue: string }
>(
  'famille/fetchAllFamillesTree',
  async ({ type }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getAll', {
        params: { type, nom: null, visible: null, limit: 1000, offset: 0 },
      });
      return response.data.data ?? [];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement de l\'arborescence.');
    }
  }
);

// ENTITÉS RATTACHÉES À UNE CATÉGORIE — lecture seule. Liste les entités du type
// "feuille" (leafType : NATURE_REGLEMENT, DEPOT, …) rattachées à une catégorie.
// Écrit dans un slot dédié (categoryEntities) pour ne pas écraser familleData.
export const fetchEntitiesByCategorie = createAsyncThunk<
  FamilleData[],
  { leafType: GeneriqueDataType; categorie_id: number },
  { rejectValue: string }
>(
  'famille/fetchEntitiesByCategorie',
  async ({ leafType, categorie_id }, thunkAPI) => {
    try {
      const response = await api.get('/familles/getByParent', {
        params: {
          type: leafType,
          parent_id: categorie_id,
          visible: null,
          limit: 1000,
          offset: 0,
        },
      });
      return response.data.data ?? [];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement des entités rattachées.');
    }
  }
);

// CREATE OR UPDATE
export const createFamille = createAsyncThunk<FamilleData, FamilleData, { rejectValue: string }>(
  'famille/createOrUpdate',
  async (familleData, thunkAPI) => {
    try {
      const response = await api.post('/familles/createOrUpdate', familleData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors de la sauvegarde.');
    }
  }
);

// Import Excel — tout ou rien ; le type (famille d'articles, catégorie…) vient de l'écran
export const importFamillesExcel = createAsyncThunk<
  { success: number; errors: Array<{ row: number; message: string }> },
  { lignes: Record<string, any>[]; type: string },
  { rejectValue: string }
>('famille/importExcel', async (payload, thunkAPI) => {
  try {
    const response = await api.post('/familles/import', payload);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l\'import'
    );
  }
});

// DELETE
export const deleteFamille = createAsyncThunk<number, number, { rejectValue: string }>(
  'famille/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/familles', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur lors de la suppression.');
    }
  }
);

const familleSlice = createSlice({
  name: 'famille',
  initialState,
  reducers: {
    setSelectedFamille: (state, action: PayloadAction<FamilleData | null>) => {
      state.selectedFamille = action.payload;
      state.tree = [];
      state.categoryEntities = [];
    },
    clearAllFamillesTree: (state) => {
      state.allFamilles = [];
      state.allFamillesType = null;
    },
    clearCategoryEntities: (state) => {
      state.categoryEntities = [];
    },
    // Réinitialise toutes les données de hiérarchie — appelé au démontage de
    // FamilleHierarchie pour éviter d'afficher la hiérarchie précédente quand on
    // change de type (familles d'articles ↔ catégories de nature de règlement).
    clearFamilleData: (state) => {
      state.familleData = [];
      state.autocompleteMain = [];
      state.autocompleteParent = [];
      state.tree = [];
      state.allFamilles = [];
      state.allFamillesType = null;
      state.categoryEntities = [];
      state.selectedFamille = null;
      state.total = 0;
      state.totalPages = 1;
      state.currentPage = 1;
    },
    clearFamilleMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearFamilleAutocomplete: (state, action: PayloadAction<FamilleAutocompleteSlot>) => {
      if (action.payload === 'main') state.autocompleteMain = [];
      else state.autocompleteParent = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFamilles
      .addCase(fetchFamilles.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFamilles.fulfilled, (state, action) => {
        state.loading = false;
        state.familleData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchFamilles.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      // fetchFamillesByParent
      .addCase(fetchFamillesByParent.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFamillesByParent.fulfilled, (state, action) => {
        state.loading = false;
        state.familleData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchFamillesByParent.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      // fetchFamillesAutocomplete
      .addCase(fetchFamillesAutocomplete.pending, (state) => { state.autocompleteLoading = true; })
      .addCase(fetchFamillesAutocomplete.fulfilled, (state, action) => {
        state.autocompleteLoading = false;
        if (action.payload.slot === 'main') state.autocompleteMain = action.payload.data;
        else state.autocompleteParent = action.payload.data;
      })
      .addCase(fetchFamillesAutocomplete.rejected, (state) => { state.autocompleteLoading = false; })

      // fetchFamilleTree
      .addCase(fetchFamilleTree.pending, (state) => { state.treeLoading = true; })
      .addCase(fetchFamilleTree.fulfilled, (state, action) => {
        state.treeLoading = false;
        state.tree = action.payload;
      })
      .addCase(fetchFamilleTree.rejected, (state, action) => { state.treeLoading = false; state.error = action.payload as string; })

      // fetchAllFamillesTree
      .addCase(fetchAllFamillesTree.pending, (state) => { state.allFamillesLoading = true; })
      .addCase(fetchAllFamillesTree.fulfilled, (state, action) => {
        state.allFamillesLoading = false;
        state.allFamilles = action.payload;
        state.allFamillesType = action.meta.arg.type;
      })
      .addCase(fetchAllFamillesTree.rejected, (state, action) => { state.allFamillesLoading = false; state.error = action.payload as string; })

      // fetchEntitiesByCategorie
      .addCase(fetchEntitiesByCategorie.pending, (state) => { state.categoryEntitiesLoading = true; })
      .addCase(fetchEntitiesByCategorie.fulfilled, (state, action) => {
        state.categoryEntitiesLoading = false;
        state.categoryEntities = action.payload;
      })
      .addCase(fetchEntitiesByCategorie.rejected, (state, action) => { state.categoryEntitiesLoading = false; state.error = action.payload as string; })

      // createFamille
      .addCase(createFamille.pending, (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(createFamille.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Famille enregistrée avec succès !';
      })
      .addCase(createFamille.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      // deleteFamille
      .addCase(deleteFamille.pending, (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(deleteFamille.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Famille supprimée.';
      })
      .addCase(deleteFamille.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { setSelectedFamille, clearAllFamillesTree, clearFamilleMessages, clearFamilleAutocomplete, clearCategoryEntities, clearFamilleData } = familleSlice.actions;
export default familleSlice.reducer;
